import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Axios, { AxiosError } from 'axios';
import { History } from 'history';
import { match } from 'react-router-dom';
import { toast } from 'react-toastify';
import Card from '../../../components/Card';
import { Spinner } from '../../../elements';
import { API_URL } from '../../../../lib/const';
import { PermissionContext } from '../../../../hoc/withPermission';
import formater from '../../common/formater';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IApplication, IApplicationStatus, IProcess } from '../types';
import ConvocationStepperModal, { ConvocationPayload } from '../components/ConvocationStepperModal';
import { filterApplications } from '../components/filterApplications';
import { convocationTargets } from '../components/convocationTargets';
import { IVacancies, IVacancyResult, vacancyResults } from '../components/vacancyResult';
import {
  coordinationApplications,
  eligibleApplications,
  isEligibleForFinalSteps,
} from '../components/finalStepTargets';
import SendToCoordinationModal from '../components/SendToCoordinationModal';
import PromoteApplicationsModal from '../components/PromoteApplicationsModal';
import DocumentReviewModal from '../components/DocumentReviewModal';
import EliminateApplicationModal from '../components/EliminateApplicationModal';
import { getImportFileError } from '../components/importFileValidation';

type TBoardRow = {
  onboarding_id: number,
  name: string,
  field_step: number,
  vacancies: number | null,
  applications: number,
  approved_waiting: number,
  enrollments_generated: number,
}

// Tom da pill de situação por status do backend.
const STATUS_TONES: { [key in IApplicationStatus]: string } = {
  'Não preenchido': 'neutral',
  'Preenchendo': 'gold',
  'Preenchido': 'teal',
  'Em análise': 'gold',
  'Com pendências': 'red',
  'Aprovado': 'green',
  'Reprovado': 'red',
  'Eliminado': 'red',
};

// Tom da pill da coluna Resultado (posição do candidato frente às vagas).
const RESULT_TONES: { [key in IVacancyResult]: string } = {
  'Classificado': 'green',
  'Classificado Excedente': 'teal',
  'Excedente': 'gold',
  '-': 'neutral',
};

// Análise final da Secretaria sobre os documentos que a Coordenação enviou.
const FINAL_REVIEW_PILLS: { [status: string]: { label: string, tone: string } } = {
  'Em análise': { label: 'Análise final pendente', tone: 'gold' },
  'Com pendências': { label: 'Pendência com a Coordenação', tone: 'red' },
  'Aprovado': { label: 'Análise final aprovada', tone: 'green' },
};

const fetchAllApplications = async (processId: string) => {
  let page = 1;
  let results: IApplication[] = [];

  while (true) {
    const response = await Axios.get(`${API_URL}/transfers/applications`, {
      params: { process: processId, page, page_size: 100 },
    });

    results = results.concat(response.data.results);

    if (!response.data.next) break;

    page += 1;
  }

  return results;
};

const TransferProcessConvokedPage = ({ history, match }: { history: History, match: match<{ id: string }> }) => {
  const id = match.params.id;

  const [loading, setLoading] = useState(true);
  const [process, setProcess] = useState<IProcess>();
  const [board, setBoard] = useState<TBoardRow[]>([]);
  const [applications, setApplications] = useState<IApplication[]>([]);
  const [importFile, setImportFile] = useState<File>();
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const [importSummary, setImportSummary] = useState('');
  const [showConvokeModal, setShowConvokeModal] = useState(false);
  const [resendTarget, setResendTarget] = useState<IApplication | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [showCoordinationModal, setShowCoordinationModal] = useState(false);
  const [showPromoteModal, setShowPromoteModal] = useState(false);
  const [reviewToken, setReviewToken] = useState<string | null>(null);
  const [eliminateTarget, setEliminateTarget] = useState<IApplication | null>(null);

  const vacancies = useMemo<IVacancies>(
    () => board.reduce((acc: IVacancies, row) => ({ ...acc, [row.onboarding_id]: row.vacancies }), {}),
    [board],
  );
  const results = useMemo(() => vacancyResults(applications, vacancies), [applications, vacancies]);
  const targets = useMemo(() => convocationTargets(applications, vacancies), [applications, vacancies]);
  const pendingTargets = useMemo(() => targets.filter(a => !a.convoked_at), [targets]);
  const visibleApplications = useMemo(
    () => filterApplications(applications, searchQuery),
    [applications, searchQuery],
  );

  const targetApplications = resendTarget ? [resendTarget] : pendingTargets;

  const eligible = useMemo(
    () => eligibleApplications(applications),
    [applications],
  );
  const selected = useMemo(
    () => eligible.filter(a => selectedIds.indexOf(a.id) >= 0),
    [eligible, selectedIds],
  );
  // O promote rejeita o lote inteiro se um id já tiver matrícula, então só vão os que faltam.
  const promotable = useMemo(() => selected.filter(a => !a.enrollment), [selected]);
  // A Coordenação só enxerga quem está dentro das vagas: o excedente selecionado fica de
  // fora do direcionamento (mas continua valendo para a matrícula).
  const coordinationTargets = useMemo(
    () => coordinationApplications(selected, results),
    [selected, results],
  );
  const allEligibleSelected = eligible.length > 0 && selected.length === eligible.length;

  const toggleSelection = (id: number) => setSelectedIds(current => (
    current.indexOf(id) >= 0 ? current.filter(x => x !== id) : current.concat(id)
  ));

  const toggleSelectAll = () => setSelectedIds(
    allEligibleSelected ? [] : eligible.map(a => a.id)
  );

  const refresh = useCallback(() => {
    setLoading(true);

    Promise.all([
      Axios.get(`${API_URL}/transfers/processes/${id}`),
      Axios.get(`${API_URL}/transfers/processes/${id}/vacancies_board`),
      fetchAllApplications(id),
    ])
      .then(([processResponse, boardResponse, applicationRows]) => {
        setProcess(processResponse.data);
        setBoard(boardResponse.data);
        setApplications(applicationRows);
        setSelectedIds([]);
      })
      .catch((error: AxiosError) => {
        if (error.response && error.response.status === 404) {
          history.push('/transferencias/processos');

        } else {
          toast.error('Ocorreu um erro ao carregar os convocados.');

        }
      })
      .finally(() => setLoading(false))
  }, [id]);

  useEffect(() => { refresh() }, [refresh]);

  const handleDownloadFinalReport = useCallback(() => {
    if (!id) return;

    Axios.get(`${API_URL}/transfers/processes/${id}/final_report`, { responseType: 'blob' })
      .then(response => {
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${process ? process.name : 'processo'} - situacao final.xlsx`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => {
        toast.error('Não foi possível baixar o relatório de situação final.');
      });
  }, [id, process]);

  const handleImport = async () => {
    if (!importFile) return;

    setSubmitting(true);
    setImportErrors([]);
    setImportSummary('');

    const data = new FormData();
    data.append('file', importFile);

    try {
      const response = await Axios.post(
        `${API_URL}/transfers/processes/${id}/import_applications`,
        data,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      const { created, skipped } = response.data;

      setImportSummary(
        `${created} candidato(s) importado(s).` +
        (skipped.length ? ` ${skipped.length} ignorado(s) (já inscritos no processo).` : '')
      );

      toast.success('Planilha importada com sucesso.');
      refresh();

    } catch (error) {
      const response = (error as AxiosError).response;

      if (response && response.status === 400 && response.data.file) {
        setImportErrors(response.data.file);
        const firstError = response.data.file[0];
        toast.error(
          firstError && firstError.includes('formato correto')
            ? firstError
            : 'A planilha contém erros. Nada foi importado.'
        );

      } else {
        toast.error('Ocorreu um erro desconhecido ao importar a planilha.');

      }

    } finally {
      setSubmitting(false);

    }
  };

  const selectImportFile = (file?: File) => {
    if (!file) return;

    const error = getImportFileError(file);
    setImportFile(error ? undefined : file);
    setImportErrors(error ? [error] : []);
    setImportSummary('');

    if (error) toast.error(error);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);

    const file = event.dataTransfer.files && event.dataTransfer.files[0];

    selectImportFile(file);
  };

  const handleConvoke = (payload: ConvocationPayload) => {
    setSubmitting(true);

    // Na convocação em massa quem escolhe os alvos é o backend (os 4x vagas melhor
    // classificados); "applications" só vai no reenvio individual.
    Axios.post(`${API_URL}/transfers/processes/${id}/convoke`, {
      ...payload,
      ...(resendTarget ? { applications: [resendTarget.id] } : {}),
    })
      .then(response => {
        const { sent, failed } = response.data as {
          sent: number,
          failed: { id: number, cpf: string, error: string }[],
        };

        if (sent > 0) {
          toast.success(`${sent} convocação(ões) enviada(s).`);
        }

        if (failed.length) {
          const reasons = Array.from(new Set(failed.map(f => f.error))).join('; ');
          toast.error(
            `${failed.length} envio(s) falharam (seguem como não convocados). Motivo: ${reasons || 'erro desconhecido'}`
          );
        }

        setShowConvokeModal(false);
        setResendTarget(null);
        refresh();
      })
      .catch((error: AxiosError) => {
        const detail = error.response && error.response.status === 400
          ? JSON.stringify(error.response.data)
          : '';

        toast.error(`Ocorreu um erro ao enviar as convocações. ${detail}`.trim());
      })
      .finally(() => setSubmitting(false))
  };

  const handleEliminate = () => {
    if (!eliminateTarget) return;

    setSubmitting(true);

    Axios.post(`${API_URL}/transfers/applications/${eliminateTarget.token}/eliminate`)
      .then(() => {
        toast.success(
          `${eliminateTarget.assumed_name || eliminateTarget.real_name} marcado(a) como Eliminado(a).`
        );
        setEliminateTarget(null);
        refresh();
      })
      .catch((error: AxiosError) => {
        const detail = error.response && error.response.status === 400
          ? JSON.stringify(error.response.data)
          : '';

        toast.error(`Não foi possível eliminar o candidato. ${detail}`.trim());
      })
      .finally(() => setSubmitting(false))
  };

  const openResend = (application: IApplication) => {
    setResendTarget(application);
    setShowConvokeModal(true);
  };

  const handleSendToCoordination = (coordinationEmail: string) => {
    setSubmitting(true);

    Axios.post(`${API_URL}/transfers/processes/${id}/send_to_coordination`, {
      applications: coordinationTargets.map(a => a.id),
      coordination_email: coordinationEmail,
    })
      .then(response => {
        toast.success(`Documentação de ${response.data.sent} candidato(s) direcionada à Coordenação.`);
        setShowCoordinationModal(false);
        refresh();
      })
      .catch((error: AxiosError) => {
        const detail = error.response && error.response.status === 400
          ? JSON.stringify(error.response.data)
          : '';

        toast.error(`Ocorreu um erro ao direcionar a documentação. ${detail}`.trim());
      })
      .finally(() => setSubmitting(false))
  };

  const handlePromote = () => {
    setSubmitting(true);

    Axios.post(`${API_URL}/transfers/applications/promote`, {
      applications: promotable.map(a => a.id),
    })
      .then(() => {
        toast.success(`${promotable.length} matrícula(s) gerada(s).`);
        setShowPromoteModal(false);
        refresh();
      })
      .catch((error: AxiosError) => {
        const detail = error.response && error.response.status === 400
          ? JSON.stringify(error.response.data)
          : '';

        toast.error(`Ocorreu um erro ao gerar as matrículas. ${detail}`.trim());
      })
      .finally(() => setSubmitting(false))
  };

  return (
    <PermissionContext.Consumer>
      {({ hasPermission }: { hasPermission: (permission: string) => boolean }) => (
        <div className="transfer-convoked-page">
          <Card
            title={(
              <>
                {`Convocados - ${process ? process.name : ''}`}
                {process && (
                  <span className={`tcp-status-pill ${process.active ? 'is-open' : 'is-closed'}`}>
                    {process.active ? 'Aberto' : 'Encerrado'}
                  </span>
                )}
              </>
            )}
          >
            <Spinner active={loading}>
              {process && (
                <p className="tcp-subtitle">
                  Curso de {process.course.name}. Importe a planilha de classificados, acompanhe o
                  placar de vagas e convoque os candidatos.
                </p>
              )}
              {process && hasPermission('transfers.view_transferapplication') && (
                <div className="text-right mb-3">
                  <button
                    className="btn btn-black"
                    type="button"
                    onClick={handleDownloadFinalReport}
                  >
                    <FontAwesomeIcon icon="download" /> Baixar relatório de situação final
                  </button>
                </div>
              )}
              {hasPermission('transfers.import_applications') && (
                <section className="tcp-section">
                  <div className="tcp-section-head">
                    <span className="tcp-kicker">Passo 1</span>
                    <h2>Importar candidatos classificados</h2>
                  </div>
                  <div
                    className={`tcp-dropzone${dragOver ? ' is-dragover' : ''}`}
                    onDragOver={(event) => { event.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={handleDrop}
                  >
                    <div className="tcp-dropzone-icon">
                      <FontAwesomeIcon icon="file-arrow-up" />
                    </div>
                    <div className="tcp-dropzone-text">
                      <strong>
                        {importFile ? importFile.name : 'Arraste a planilha .xlsx aqui ou selecione um arquivo'}
                      </strong>
                      <span>
                        Aceita o export do portal do candidato sem edição. Colunas:{' '}
                        <b>Nome (ou ALUNO), CPF, E-mail (ou EMAIL), Classificação (ou POSIÇÃO)</b>{' '}
                        · Ano/Período de Promoção (ou CURSO), exigida só quando o processo tem mais
                        de um período · opcionais: Nome Social, Telefone (ou TEL1), Data de
                        Nascimento (ou DTNASC).
                      </span>
                    </div>
                    <div className="tcp-dropzone-actions">
                      <label className="tcp-file-button">
                        Escolher arquivo
                        <input
                          type="file"
                          accept=".xlsx"
                          onChange={(event) => {
                            selectImportFile(event.target.files ? event.target.files[0] : undefined);
                            event.target.value = '';
                          }}
                        />
                      </label>
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={!importFile || submitting}
                        onClick={handleImport}
                      >
                        Importar planilha
                      </button>
                    </div>
                  </div>
                  {importSummary && <p className="tcp-import-summary">{importSummary}</p>}
                  {importErrors.length > 0 && (
                    <ul className="tcp-import-errors">
                      {importErrors.map(error => <li key={error}>{error}</li>)}
                    </ul>
                  )}
                </section>
              )}
              <section className="tcp-section">
                <div className="tcp-section-head">
                  <h2>Placar de vagas</h2>
                </div>
                <p className="tcp-section-note">
                  O sistema não bloqueia inscrições nem a geração de matrículas por vaga. O
                  direcionamento à Coordenação, sim: ela só enxerga quem está dentro das vagas.
                </p>
                {board.map(row => (
                  <div key={row.onboarding_id} className="tcp-board-group">
                    {board.length > 1 && <h3 className="tcp-board-name">{row.name}</h3>}
                    <div className="tcp-metrics">
                      <div className="tcp-metric tcp-metric-gold">
                        <div className="tcp-metric-top"><span>Vagas</span><i className="tcp-metric-dot" /></div>
                        <strong>{row.vacancies === null ? '-' : row.vacancies}</strong>
                      </div>
                      <div className="tcp-metric tcp-metric-teal">
                        <div className="tcp-metric-top"><span>Inscrições</span><i className="tcp-metric-dot" /></div>
                        <strong>{row.applications}</strong>
                      </div>
                      <div className="tcp-metric tcp-metric-gold">
                        <div className="tcp-metric-top"><span>Aprovados aguardando</span><i className="tcp-metric-dot" /></div>
                        <strong>{row.approved_waiting}</strong>
                      </div>
                      <div className="tcp-metric tcp-metric-green">
                        <div className="tcp-metric-top"><span>Matrículas geradas</span><i className="tcp-metric-dot" /></div>
                        <strong>{row.enrollments_generated}</strong>
                      </div>
                    </div>
                  </div>
                ))}
              </section>
              <section className="tcp-section">
                <div className="tcp-candidates-head">
                  <div>
                    <span className="tcp-kicker">Passo 2</span>
                    <h2>
                      Candidatos
                      {applications.length > 0 && (
                        <span className="tcp-count">{applications.length} classificado(s)</span>
                      )}
                    </h2>
                  </div>
                  <div className="tcp-search">
                    <FontAwesomeIcon icon="magnifying-glass" />
                    <input
                      placeholder="Buscar por nome ou CPF"
                      value={searchQuery}
                      onChange={(event) => setSearchQuery(event.target.value)}
                    />
                  </div>
                </div>
                <div className="tcp-table-wrap">
                  {visibleApplications.length > 0 ? (
                    <table className="tcp-table">
                      <thead>
                        <tr>
                          <th className="tcp-col-check">
                            <input
                              type="checkbox"
                              checked={allEligibleSelected}
                              disabled={eligible.length === 0}
                              onChange={toggleSelectAll}
                              title="Selecionar todos os aprovados"
                            />
                          </th>
                          <th>Nome</th>
                          <th>CPF</th>
                          <th>Classif.</th>
                          <th>Resultado</th>
                          <th>Situação</th>
                          <th>Convocado em</th>
                          <th>Coordenação</th>
                          <th>Matrícula</th>
                          <th className="tcp-col-actions">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleApplications.map(application => (
                          <tr key={application.id}>
                            <td className="tcp-col-check">
                              <input
                                type="checkbox"
                                checked={selectedIds.indexOf(application.id) >= 0}
                                disabled={!isEligibleForFinalSteps(application)}
                                onChange={() => toggleSelection(application.id)}
                                title={
                                  isEligibleForFinalSteps(application)
                                    ? 'Selecionar candidato'
                                    : 'Aguardando a aprovação da documentação pela Secretaria'
                                }
                              />
                            </td>
                            <td>
                              <div className="tcp-cand-name">{application.assumed_name || application.real_name}</div>
                              <div className="tcp-cand-email">{application.email}</div>
                            </td>
                            <td>{formater.cpf(application.cpf)}</td>
                            <td>
                              {application.classification != null
                                ? <span className="tcp-rank">{application.classification}º</span>
                                : '-'}
                            </td>
                            <td>
                              <span className={`tcp-pill tcp-pill-${RESULT_TONES[results[application.id]] || 'neutral'}`}>
                                {results[application.id] || '-'}
                              </span>
                            </td>
                            <td>
                              <span className={`tcp-pill tcp-pill-${STATUS_TONES[application.status] || 'neutral'}`}>
                                {application.status}
                              </span>
                            </td>
                            <td>
                              {application.convoked_at || <span className="tcp-pill tcp-pill-neutral">Não convocado</span>}
                              <div className="tcp-cell-note">
                                Data limite: {application.submission_deadline || '-'}
                              </div>
                            </td>
                            <td>
                              {application.curriculum_adaptation_document_id ? (
                                <span className="tcp-pill tcp-pill-green">Recebida</span>
                              ) : application.coordination_sent_at ? (
                                <span className="tcp-pill tcp-pill-gold">
                                  Enviada {application.coordination_sent_at}
                                </span>
                              ) : '-'}
                              {application.coordination_documents_step_status
                                && FINAL_REVIEW_PILLS[application.coordination_documents_step_status] && (
                                <div className="mt-1">
                                  <span className={`tcp-pill tcp-pill-${FINAL_REVIEW_PILLS[application.coordination_documents_step_status].tone}`}>
                                    {FINAL_REVIEW_PILLS[application.coordination_documents_step_status].label}
                                  </span>
                                </div>
                              )}
                            </td>
                            <td>
                              {application.enrollment
                                ? <span className="tcp-pill tcp-pill-green">{application.enrollment.status}</span>
                                : '-'}
                            </td>
                            <td className="tcp-col-actions">
                              {hasPermission('transfers.view_transferapplication') && (
                                application.coordination_documents_step_status === 'Em análise' ? (
                                  <button
                                    type="button"
                                    className="tcp-icon-btn"
                                    title="Fazer a análise final dos documentos da Coordenação"
                                    onClick={() => setReviewToken(application.token)}
                                  >
                                    <FontAwesomeIcon icon="check-circle" />
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    className="tcp-icon-btn"
                                    title="Abrir a análise documental deste candidato"
                                    onClick={() => setReviewToken(application.token)}
                                  >
                                    <FontAwesomeIcon icon="eye" />
                                  </button>
                                )
                              )}
                              {hasPermission('transfers.convoke_applications') && (
                                <button
                                  type="button"
                                  className="tcp-icon-btn"
                                  title={
                                    !application.convoked_at
                                      ? 'Candidato ainda não convocado'
                                      : (process && process.convocation_email_message)
                                        ? 'Reenviar e-mail de convocação'
                                        : 'Envie a convocação uma vez para registrar a mensagem'
                                  }
                                  disabled={
                                    !application.convoked_at
                                    || !(process && process.convocation_email_message)
                                    || submitting
                                  }
                                  onClick={() => openResend(application)}
                                >
                                  <FontAwesomeIcon icon="envelope" />
                                </button>
                              )}
                              {hasPermission('transfers.approve_application_document_data') && (
                                application.status !== 'Eliminado' && !application.enrollment && (
                                  <button
                                    type="button"
                                    className="tcp-icon-btn"
                                    title="Marcar como Eliminado (não enviou os documentos ou não atendeu ao edital no prazo)"
                                    disabled={submitting}
                                    onClick={() => setEliminateTarget(application)}
                                  >
                                    <FontAwesomeIcon icon="user-slash" />
                                  </button>
                                )
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="tcp-empty">
                      <div className="tcp-empty-icon">
                        <FontAwesomeIcon icon="file" />
                      </div>
                      {applications.length === 0 ? (
                        <>
                          <strong>Nenhum candidato importado</strong>
                          <span>Importe uma planilha .xlsx no Passo 1 para listar os classificados aqui.</span>
                        </>
                      ) : (
                        <>
                          <strong>Nenhum candidato encontrado</strong>
                          <span>Ajuste a busca por nome ou CPF.</span>
                        </>
                      )}
                    </div>
                  )}
                </div>
                {hasPermission('transfers.convoke_applications') && (
                  <div className="tcp-actions">
                    <div className="tcp-actions-note">
                      Serão convocados os <b>4× as vagas de cada período</b> melhor classificados:{' '}
                      <b>{targets.length} candidato(s)</b>, {pendingTargets.length} ainda não convocado(s).
                    </div>
                    <div className="tcp-actions-right">
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={pendingTargets.length === 0 || submitting}
                        onClick={() => { setResendTarget(null); setShowConvokeModal(true); }}
                      >
                        <FontAwesomeIcon icon="paper-plane" />
                        Enviar convocação
                      </button>
                    </div>
                  </div>
                )}
                {(hasPermission('transfers.convoke_applications') || hasPermission('transfers.promote_application')) && (
                  <div className="tcp-actions">
                    <div className="tcp-actions-note">
                      <b>{selected.length} selecionado(s)</b> de {eligible.length} aprovado(s). Só
                      candidatos com a documentação aprovada pela Secretaria podem ser selecionados.
                      Para a Coordenação vão apenas os que estão <b>dentro do número de vagas</b>.
                    </div>
                    <div className="tcp-actions-right">
                      {hasPermission('transfers.convoke_applications') && (
                        <button
                          type="button"
                          className="btn btn-black tcp-coordination-btn"
                          disabled={coordinationTargets.length === 0 || submitting}
                          onClick={() => setShowCoordinationModal(true)}
                        >
                          <FontAwesomeIcon icon="share" />
                          Direcionar à Coordenação ({coordinationTargets.length})
                        </button>
                      )}
                      {hasPermission('transfers.promote_application') && (
                        <button
                          type="button"
                          className="btn btn-primary"
                          disabled={promotable.length === 0 || submitting}
                          onClick={() => setShowPromoteModal(true)}
                        >
                          <FontAwesomeIcon icon="user-graduate" />
                          Gerar matrícula ({promotable.length})
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </section>
            </Spinner>
            <ConvocationStepperModal
              show={showConvokeModal}
              submitting={submitting}
              targetApplications={targetApplications}
              process={process}
              isResend={!!resendTarget}
              reachLabel={
                `Serão convocados ${pendingTargets.length} candidato(s), 4× as vagas de cada período.`
              }
              onClose={() => { setShowConvokeModal(false); setResendTarget(null); }}
              onConfirm={handleConvoke}
            />
            <SendToCoordinationModal
              show={showCoordinationModal}
              submitting={submitting}
              applications={coordinationTargets}
              process={process}
              onClose={() => setShowCoordinationModal(false)}
              onConfirm={handleSendToCoordination}
            />
            <PromoteApplicationsModal
              show={showPromoteModal}
              submitting={submitting}
              applications={promotable}
              onClose={() => setShowPromoteModal(false)}
              onConfirm={handlePromote}
            />
            <EliminateApplicationModal
              show={!!eliminateTarget}
              submitting={submitting}
              application={eliminateTarget}
              onClose={() => setEliminateTarget(null)}
              onConfirm={handleEliminate}
            />
            {reviewToken && (
              // Ao fechar, recarrega a lista: a análise pode ter mudado situação,
              // pendências ou gerado matrícula.
              <DocumentReviewModal
                token={reviewToken}
                history={history}
                onClose={() => { setReviewToken(null); refresh(); }}
              />
            )}
          </Card>
        </div>
      )}
    </PermissionContext.Consumer>
  );
};

export default TransferProcessConvokedPage;
